import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';

import { DatePicker } from 'components/date-picker';
import { Btn } from 'components/btn/Btn';
import { useFocusMonitor } from 'hooks/useFocusMonitor';
import MaskedInput, { MaskedInputProps } from 'react-text-mask';
import { MASK_DATE } from 'common/modules/mask-text/masks';
import { getDaysInMonth } from 'common/utils/utils';
import { createStyles } from 'utils/createStyle';
import { LinkBtn } from 'components/link-btn';

export type CustomRangeDatePickerProps = {
    open: boolean;
    onChange: (startDate: Date | undefined, endDate: Date | undefined) => void;
    onClose: () => void;
    applyFilterBtn?: string;
};

const styles = createStyles({
    dataPickerInput: [
        'text-center border border-grey-3 rounded',
        {
            width: '120px',
        },
    ],
});

// NOTE: controls datepicker available months and dates to pick from
const maxDate = new Date();
const minDate = new Date();
minDate.setMonth(minDate.getMonth() - 18);
minDate.setDate(minDate.getDate() + 1);

export const CustomRangeDatePicker = ({
    open,
    onChange,
    onClose,
    applyFilterBtn = 'Apply Filter',
}: CustomRangeDatePickerProps) => {
    const [start, setStart] = useState<Date | null>();
    const [end, setEnd] = useState<Date | null>();

    const clearAll = (e: MouseEvent) => {
        setStart(undefined);
        setEnd(undefined);
        e.preventDefault();
    };

    const onDateRangeChanged =
        (isMobile: boolean) =>
        ([newStart, newEnd]: [Date | null, Date | null]) => {
            setStart(newStart);
            setEnd(newEnd);

            if (!isMobile && newStart && newEnd) {
                onChange(newStart, newEnd);
            }
        };

    const selectedRange = useMemo(() => {
        const startDate = start?.toLocaleDateString();
        const endDate = end?.toLocaleDateString();

        if (!startDate) {
            return 'Select start date';
        }
        if (!endDate) {
            return 'Select end date';
        }

        return `Start: ${startDate}<br />End: ${endDate}`;
    }, [start, end]);

    const applyFilter = useCallback(() => {
        if (start && end) {
            onChange(start, end);
            onClose();
        }
    }, [start, end, onChange, onClose]);

    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (open && containerRef.current) {
            const el = containerRef.current.querySelector('.customBtn') as HTMLInputElement;
            el?.focus();
        }
    }, [open, containerRef]);

    const onStartDateChange = useCallback(
        (date: Date) => {
            setStart(date);
        },
        [setStart]
    );

    const onEndDateChange = useCallback(
        (date: Date) => {
            setEnd(date);
        },
        [setEnd]
    );

    const onApply = useCallback(() => {
        if (start && end) {
            onChange(start, end);
            onClose();
        }
    }, [start, end, onChange, onClose]);

    const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
    const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);

    const dtStartRef = useRef<HTMLDivElement | null>(null);
    const dtEndRef = useRef<HTMLDivElement | null>(null);

    const startDatePickerHasFocus = useFocusMonitor(dtStartRef);
    const endDatePickerHasFocus = useFocusMonitor(dtEndRef);

    const isValidRange = useMemo(() => {
        if (!start || !end) {
            return false;
        }

        if (start > end) {
            return false;
        }

        if (start > maxDate || end > maxDate) {
            return false;
        }

        return true;
    }, [start, end]);

    return (
        <>
            {open && (
                <div ref={containerRef}>
                    <div className="block sm:hidden">
                        <div className="react-datepicker__portal select-none">
                            <div className="relative bg-white/80 p-4 rounded">
                                <DatePicker
                                    inline
                                    onChange={onDateRangeChanged(true)}
                                    selectsRange
                                    startDate={start}
                                    endDate={end}
                                    maxDate={maxDate}
                                    minDate={minDate}
                                    customInput={<span>Custom...</span>}
                                />
                                <div
                                    className="block text-center text-lg text-brand-dark font-bold py-2"
                                    dangerouslySetInnerHTML={{ __html: selectedRange }}
                                />
                                <div className="block text-center">
                                    <div className="inline-block">
                                        <div className="block mb-2 mt-4">
                                            <Btn disabled={!start || !end} onClick={applyFilter}>
                                                {applyFilterBtn}
                                            </Btn>
                                        </div>
                                        <LinkBtn className="py-4" onClick={clearAll}>
                                            Clear all dates
                                        </LinkBtn>
                                    </div>
                                </div>
                                <div className="absolute top-0 right-0">
                                    <button
                                        onClick={onClose}
                                        aria-label="Close date picker"
                                        className="px-3 py-2 bg-white rounded transform translate-x-3 -translate-y-3"
                                    >
                                        <FontAwesomeIcon
                                            className="bg-white text-brand rounded-full"
                                            icon={faTimes}
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="hidden sm:block py-1">
                        <div className="flex gap-2">
                            <div ref={dtStartRef} id="dt-start">
                                <div className="text-sm text-left">Start date:</div>
                                <DatePicker
                                    selected={start}
                                    onChange={onStartDateChange}
                                    selectsStart
                                    startDate={start}
                                    endDate={end}
                                    minDate={minDate}
                                    maxDate={new Date()}
                                    customInput={<MaskedDateInput />}
                                    open={isStartDatePickerOpen && startDatePickerHasFocus}
                                    onCalendarOpen={() => setIsStartDatePickerOpen(true)}
                                    onCalendarClose={() => setIsStartDatePickerOpen(false)}
                                />
                            </div>
                            <div ref={dtEndRef}>
                                <div className="text-sm text-left">End date:</div>
                                <DatePicker
                                    selected={end}
                                    onChange={onEndDateChange}
                                    selectsEnd
                                    startDate={start}
                                    endDate={end}
                                    minDate={minDate}
                                    maxDate={!start ? new Date() : maxDate}
                                    customInput={<MaskedDateInput />}
                                    open={isEndDatePickerOpen && endDatePickerHasFocus}
                                    onCalendarOpen={() => setIsEndDatePickerOpen(true)}
                                    onCalendarClose={() => setIsEndDatePickerOpen(false)}
                                />
                            </div>
                            <div className="flex items-end">
                                <Btn
                                    className="border border-brand rounded p-2 bg-brand text-white hover:bg-white hover:text-brand-dark"
                                    disabled={!isValidRange}
                                    isSmall={true}
                                    onClick={onApply}
                                >
                                    Apply
                                </Btn>
                                <Btn
                                    className="border border-grey-3 rounded p-2 text-grey-1 hover:text-brand-dark ml-1"
                                    isSmall={true}
                                    isSecondary={true}
                                    onClick={() => onClose()}
                                >
                                    Cancel
                                </Btn>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const MaskedDateInput = ({ value, ...props }: Omit<MaskedInputProps, 'mask'>) => {
    const valueStr = (value ?? '').toString().replace(/_/g, '');

    const [monthStr = null, dateStr = null, yearStr = null] = valueStr
        .split('/')
        .map((t) => t.trim());

    const month = useMemo(() => (monthStr ? parseInt(monthStr, 10) : null), [monthStr]);
    const date = useMemo(() => (dateStr ? parseInt(dateStr, 10) : null), [dateStr]);
    const year = useMemo(() => (yearStr ? parseInt(yearStr, 10) : null), [yearStr]);

    const isValidMonth = useMemo(() => month === null || (month > 0 && month <= 12), [month]);
    const isValidDate = useMemo(
        () =>
            date !== null && month !== null && year !== null
                ? date > 0 && date <= getDaysInMonth(month, year)
                : date === null || (date > 0 && date <= 31),
        [date, month, year]
    );
    const isValidYear = useMemo(() => year === null || year > 0, [year]);

    const isValid = useMemo(
        () => isValidDate && isValidMonth && isValidYear,
        [isValidDate, isValidMonth, isValidYear]
    );

    return (
        <MaskedInput
            guide={true}
            value={value}
            keepCharPositions={true}
            style={{
                border: `${isValid ? '' : '1px solid red'}`,
                outline: `${isValid ? '' : '1px solid red'}`,
            }}
            {...props}
            mask={MASK_DATE}
            className={`customBtn inline-block ${styles.dataPickerInput}`}
            placeholder="mm/dd/yyyy"
        />
    );
};
